import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useCallback } from "react";
import { Button } from "@project-mgmt/ui";
import { useFileUpload, useFileValidation } from "../hooks/useFileOperations";
export function FileUpload({ workspaceId, taskId, onUploadComplete, onUploadError, maxFileSize, allowedTypes, multiple = true, disabled = false, }) {
    const fileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const { uploadFiles, uploadProgress, isUploading } = useFileUpload(workspaceId, taskId);
    const { validateFiles } = useFileValidation();
    const handleFileSelect = useCallback(async (files) => {
        if (!files.length)
            return;
        // Validate files
        const validationResults = validateFiles(files);
        const hasErrors = validationResults.some((result) => !result.isValid);
        if (hasErrors) {
            const errors = validationResults.filter((result) => !result.isValid).flatMap((result) => result.errors);
            onUploadError?.(errors.join(", "));
            return;
        }
        try {
            const results = await uploadFiles(files);
            const successfulUploads = results.filter((result) => result.success);
            const failedUploads = results.filter((result) => !result.success);
            if (successfulUploads.length > 0) {
                successfulUploads.forEach((result) => {
                    if (result.file) {
                        onUploadComplete?.(result.file);
                    }
                });
            }
            if (failedUploads.length > 0) {
                const errors = failedUploads.map((result) => result.error).join(", ");
                onUploadError?.(errors);
            }
        }
        catch (error) {
            onUploadError?.(error instanceof Error ? error.message : "Upload failed");
        }
    }, [uploadFiles, validateFiles, onUploadComplete, onUploadError]);
    const handleInputChange = useCallback((event) => {
        const files = event.target.files;
        if (files) {
            handleFileSelect(files);
        }
        // Reset input value to allow selecting the same file again
        event.target.value = "";
    }, [handleFileSelect]);
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        setIsDragOver(true);
    }, []);
    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        setIsDragOver(false);
    }, []);
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        setIsDragOver(false);
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files);
        }
    }, [handleFileSelect]);
    const openFileDialog = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    const progressEntries = Object.values(uploadProgress);
    const hasActiveUploads = progressEntries.length > 0;
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: `rounded-lg border-2 border-dashed p-6 text-center transition-colors ${isDragOver ? "border-blue-400 bg-blue-50" : "border-gray-300"} ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-gray-400"} `, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, onClick: disabled ? undefined : openFileDialog, children: [_jsx("input", { ref: fileInputRef, type: "file", multiple: multiple, accept: allowedTypes?.join(","), onChange: handleInputChange, disabled: disabled, className: "hidden" }), _jsxs("div", { className: "space-y-2", children: [_jsx("div", { className: "text-4xl", children: "\uD83D\uDCCE" }), _jsx("div", { className: "text-sm text-gray-600", children: isDragOver ? ("Drop files here") : (_jsxs(_Fragment, { children: ["Drop files here or ", _jsx("span", { className: "text-blue-600 underline", children: "browse" })] })) }), maxFileSize && (_jsxs("div", { className: "text-xs text-gray-500", children: ["Max file size: ", Math.round(maxFileSize / 1024 / 1024), "MB"] }))] })] }), _jsx("div", { className: "flex justify-center", children: _jsx(Button, { onClick: openFileDialog, disabled: disabled || isUploading, variant: "outline", children: isUploading ? "Uploading..." : "Select Files" }) }), hasActiveUploads && (_jsxs("div", { className: "space-y-2", children: [_jsx("h4", { className: "text-sm font-medium text-gray-700", children: "Upload Progress" }), progressEntries.map((progress) => (_jsxs("div", { className: "space-y-1", children: [_jsxs("div", { className: "flex justify-between text-sm", children: [_jsx("span", { className: "mr-2 flex-1 truncate", children: progress.filename }), _jsx("span", { className: "text-gray-500", children: progress.status === "completed"
                                            ? "✅"
                                            : progress.status === "error"
                                                ? "❌"
                                                : `${progress.progress}%` })] }), progress.status === "uploading" && (_jsx("div", { className: "h-2 w-full rounded-full bg-gray-200", children: _jsx("div", { className: "h-2 rounded-full bg-blue-600 transition-all duration-300", style: { width: `${progress.progress}%` } }) })), progress.status === "error" && progress.error && (_jsx("div", { className: "text-xs text-red-600", children: progress.error }))] }, progress.fileId)))] }))] }));
}
